import React, { useState, useMemo } from 'react';

const MessageInput = ({ onSendMessage, disabled, messages = [], currentUserId }) => {
    const [message, setMessage] = useState('');
    const [showPromptPopup, setShowPromptPopup] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (message.trim() && !disabled) {
            onSendMessage(message.trim());
            setMessage('');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    // Generate dynamic prompts based on recent messages
    const dynamicPrompts = useMemo(() => {
        if (!messages || messages.length === 0) {
            return [
                "Hi, motherfucker!",
                "This is fucking terrible!",
                "You're such an asshole!",
                "I hate this shit!",
                "What the hell is wrong with you?",
                "This sucks big time!",
                "You're being a real jerk!",
                "I'm so pissed off!",
                "This is complete bullshit!",
                "You're driving me crazy!"
            ];
        }

        // Get the last few messages from other users
        const otherUserMessages = messages
            .filter(msg => msg.userId !== currentUserId && msg.text)
            .slice(-3) // Last 3 messages from other users
            .map(msg => msg.text);

        // Generate contextual prompts based on what others said
        const contextualPrompts = [];
        
        otherUserMessages.forEach(msg => {
            if (!msg || typeof msg !== 'string') return;
            const lowerMsg = msg.toLowerCase();
            
            // If they mentioned something positive, respond negatively
            if (lowerMsg.includes('great') || lowerMsg.includes('awesome') || lowerMsg.includes('amazing')) {
                contextualPrompts.push("That's fucking bullshit!");
                contextualPrompts.push("You're full of shit!");
                contextualPrompts.push("This is complete garbage!");
            }
            
            // If they asked a question, respond rudely
            if (lowerMsg.includes('?') || lowerMsg.includes('how') || lowerMsg.includes('what')) {
                contextualPrompts.push("What the hell are you talking about?");
                contextualPrompts.push("That's a stupid question!");
                contextualPrompts.push("I don't give a fuck!");
            }
            
            // If they mentioned work/stress, respond with frustration
            if (lowerMsg.includes('work') || lowerMsg.includes('busy') || lowerMsg.includes('tired')) {
                contextualPrompts.push("Work is fucking killing me!");
                contextualPrompts.push("I'm so done with this shit!");
                contextualPrompts.push("This job is driving me insane!");
            }
            
            // If they mentioned food, respond negatively
            if (lowerMsg.includes('food') || lowerMsg.includes('eat') || lowerMsg.includes('hungry')) {
                contextualPrompts.push("This food tastes like shit!");
                contextualPrompts.push("I hate this restaurant!");
                contextualPrompts.push("The service here is terrible!");
            }
            
            // If they mentioned weather, complain
            if (lowerMsg.includes('weather') || lowerMsg.includes('rain') || lowerMsg.includes('sunny')) {
                contextualPrompts.push("This weather is fucking awful!");
                contextualPrompts.push("I hate this shitty weather!");
                contextualPrompts.push("Why is it always raining?");
            }
        });

        // If no contextual prompts were generated, use default ones
        if (contextualPrompts.length === 0) {
            return [
                "Hi, motherfucker!",
                "This is fucking terrible!",
                "You're such an asshole!",
                "I hate this shit!",
                "What the hell is wrong with you?",
                "This sucks big time!",
                "You're being a real jerk!",
                "I'm so pissed off!",
                "This is complete bullshit!",
                "You're driving me crazy!"
            ];
        }

        // Remove duplicates and limit to 10 prompts
        const uniquePrompts = [...new Set(contextualPrompts)];
        return uniquePrompts.slice(0, 10);
    }, [messages, currentUserId]);

    // Generate dynamic button text based on recent messages
    const getButtonText = () => {
        if (!messages || messages.length === 0) {
            return "💬 Say whatever you want - AI will make it good vibes!";
        }

        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.userId !== currentUserId && lastMessage.text) {
            return "💥 They said something - now let it all out! AI will make it good vibes!";
        }
        
        return "💬 Say whatever you want - AI will make it good vibes!";
    };

    const handlePromptClick = (prompt) => {
        setMessage(prompt);
        setShowPromptPopup(false);
    };

    return (
        <div className="message-input-container">
            <div className="prompt-suggestion">
                <button
                    className="prompt-trigger"
                    onClick={() => setShowPromptPopup(!showPromptPopup)}
                    disabled={disabled}
                >
                    {getButtonText()}
                </button>

                {showPromptPopup && (
                    <div className="prompt-popup">
                        <div className="prompt-header">
                            <span>💥 Let it all out! AI will transform your words into good vibes:</span>
                            <button
                                className="close-prompt"
                                onClick={() => setShowPromptPopup(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="prompt-grid">
                            {dynamicPrompts.map((prompt, index) => (
                                <button
                                    key={index}
                                    className="prompt-option"
                                    onClick={() => handlePromptClick(prompt)}
                                >
                                    {prompt}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <form onSubmit={handleSubmit} className="message-form">
                <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={disabled ? "Transforming message..." : "Say whatever you want - AI will turn it into good vibes! 😊"}
                    disabled={disabled}
                    className="message-input"
                    onKeyPress={handleKeyPress}
                />
                <button
                    type="submit"
                    disabled={!message.trim() || disabled}
                    className="send-button"
                >
                    Send
                </button>
            </form>
        </div>
    );
};

export default MessageInput;
